import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, PrintIcon } from "../../../Icons";
import { DOCUMENT_BTN, DOCUMENT_THEME } from "../documentTheme";

/**
 * Bulk actions control for document tables.
 * Extensible: pass additional `actions` later (export, archive, …).
 *
 * @param {{
 *   selectedCount: number,
 *   disabled?: boolean,
 *   loading?: boolean,
 *   onPrint: () => void | Promise<void>,
 *   actions?: Array<{ id: string, label: string, onClick: () => void, icon?: React.ReactNode }>,
 * }} props
 */
const DocumentBulkActions = ({
  selectedCount,
  disabled = false,
  loading = false,
  onPrint,
  actions,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (loading) setOpen(false);
  }, [loading]);

  const menuItems =
    actions && actions.length > 0
      ? actions
      : [
          {
            id: "print",
            label: "Print",
            icon: <PrintIcon size={16} />,
            onClick: onPrint,
          },
        ];

  const isDisabled = disabled || loading || selectedCount === 0;

  return (
    <div className="relative mb-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {loading
            ? `Preparing ${selectedCount} document${selectedCount === 1 ? "" : "s"}…`
            : selectedCount > 0
              ? `${selectedCount} selected`
              : "Select documents to enable actions"}
        </p>
        <div className="relative" ref={rootRef}>
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => setOpen((v) => !v)}
            className={`${DOCUMENT_BTN.modalCancel} gap-2`}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-busy={loading}
          >
            {loading ? (
              <>
                <span
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"
                  aria-hidden="true"
                />
                Preparing…
              </>
            ) : (
              <>
                Actions
                <ChevronDownIcon
                  size={16}
                  className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </>
            )}
          </button>
          {open && !loading ? (
            <div
              role="menu"
              className={`absolute right-0 z-30 mt-1.5 min-w-[11rem] overflow-hidden rounded-lg border bg-white py-1 shadow-lg ${DOCUMENT_THEME.section.wrap}`}
            >
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm text-slate-800 hover:bg-indigo-50"
                  onClick={async () => {
                    setOpen(false);
                    await item.onClick?.();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div
          className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white"
          aria-live="polite"
          aria-label="Preparing print preview"
        >
          <div className="animate-pulse space-y-3 px-4 py-4">
            <div className="h-3 w-1/3 rounded bg-slate-200" />
            <div className="h-3 w-full rounded bg-slate-100" />
            <div className="h-3 w-5/6 rounded bg-slate-100" />
            <div className="h-3 w-2/3 rounded bg-slate-100" />
            <div className="mt-2 grid grid-cols-3 gap-2">
              <div className="h-16 rounded bg-slate-100" />
              <div className="h-16 rounded bg-slate-100" />
              <div className="h-16 rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DocumentBulkActions;
